import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { daysUntilCalendar, formatDateOnly } from '../lib/dateOnly';
import { deadlineStateLabel } from '../lib/format';

/* ============================================================
   Dialog focus management (WCAG 2.4.3 / 2.1.2)
   Focus trap + Esc + initial focus + return focus.
   ============================================================ */
export function useDialogFocus(open: boolean, onClose: () => void) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const prevFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    prevFocus.current = document.activeElement as HTMLElement | null;
    const node = dialogRef.current;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !node) return;
      const focusables = Array.from(
        node.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.getClientRects().length > 0);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => {
      // R2-BR-D67: fields before buttons. The previous selector took
      // the FIRST focusable element in DOM order — in a dialog that is
      // the header's ✕ close button, so a keyboard user pressing Space
      // (or the browser tester typing word spaces) "clicked" Close and
      // lost the dialog mid-typing. A dialog's first FIELD is the safe
      // landing spot; buttons are the fallback for field-less dialogs.
      const target =
        node?.querySelector<HTMLElement>('[data-autofocus]') ??
        node?.querySelector<HTMLElement>(
          'input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
        ) ??
        node?.querySelector<HTMLElement>(
          'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        );
      if (target) target.focus();
      else node?.focus();
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = '';
      prevFocus.current?.focus?.();
    };
  }, [open, onClose]);

  return dialogRef;
}

/* ============================================================
   Buttons
   ============================================================ */
type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'teal';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
}

export function Button({ variant = 'secondary', size = 'md', block, className = '', ...rest }: ButtonProps) {
  const cls = [
    'btn',
    variant === 'primary' ? 'btn-primary'
      : variant === 'danger' ? 'btn-danger'
      : variant === 'ghost' ? 'btn-ghost'
      : variant === 'teal' ? 'btn-teal'
      : 'btn-secondary',
    size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : '',
    block ? 'btn-block' : '',
    className,
  ].filter(Boolean).join(' ');
  return <button className={cls} {...rest} />;
}

export const IconBtn = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }>(
  function IconBtn({ label, className = '', children, ...rest }, ref) {
    return (
      <button type="button" ref={ref} className={`icon-btn ${className}`} aria-label={label} title={label} {...rest}>
        {children}
      </button>
    );
  },
);

/* ============================================================
   Badge
   ============================================================ */
type BadgeTone = 'info' | 'warning' | 'danger' | 'success' | 'navy' | 'neutral' | 'stage' | 'gold' | 'unknown' | 'violet';

export function Badge({ tone = 'neutral', dot, className = '', children }: { tone?: BadgeTone; dot?: boolean; className?: string; children: React.ReactNode }) {
  return <span className={`badge badge-${tone} ${className}`}>{dot && <span className="badge-dot" aria-hidden="true" />}{children}</span>;
}

/* ============================================================
   Card
   ============================================================ */
export function Card({ hover, className = '', children, ...rest }: React.HTMLAttributes<HTMLDivElement> & { hover?: boolean }) {
  return <div className={`card${hover ? ' card-hover' : ''} ${className}`} {...rest}>{children}</div>;
}

export function CardHead({ title, sub, actions, titleRef }: { title: React.ReactNode; sub?: React.ReactNode; actions?: React.ReactNode; titleRef?: React.Ref<HTMLDivElement> }) {
  return (
    <div className="card-head">
      <div>
        <div className="card-title" ref={titleRef} tabIndex={titleRef ? -1 : undefined}>{title}</div>
        {sub && <div className="card-sub">{sub}</div>}
      </div>
      {actions && <div className="stack" style={{ flexDirection: 'row' }}>{actions}</div>}
    </div>
  );
}

export function Panel({ className = '', children, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={`panel ${className}`} {...rest}>{children}</div>;
}

/* ============================================================
   Forms
   ============================================================ */
export function Field({ label, hint, error, required, children, className = '' }: {
  label?: React.ReactNode; hint?: React.ReactNode; error?: React.ReactNode; required?: boolean;
  children: React.ReactNode; className?: string;
}) {
  const autoId = useId();
  const hintId = useId();
  const errorId = useId();
  // Associate a visible <label> with its control (WCAG 1.3.1 / 3.3.2) by
  // injecting an id + describedby into a single-element child.
  let control = children;
  let controlId: string | undefined;
  if (React.isValidElement(children)) {
    const props = children.props as { id?: string; 'aria-describedby'?: string };
    controlId = props.id ?? `field-${autoId}`;
    const describedBy = [
      props['aria-describedby'],
      hint ? `hint-${hintId}` : null,
      error ? `err-${errorId}` : null,
    ].filter(Boolean).join(' ') || undefined;
    control = React.cloneElement(children, {
      id: controlId,
      ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      ...(error ? { 'aria-invalid': true } : {}),
    } as Record<string, unknown>);
  }
  return (
    <div className={`field ${className}`}>
      {label && <label className="field-label" htmlFor={controlId}>{label}{required && <span className="required-mark" aria-hidden="true"> *</span>}</label>}
      {control}
      {hint && <div className="field-hint" id={`hint-${hintId}`}>{hint}</div>}
      {error && <div className="field-error" id={`err-${errorId}`} role="alert">{error}</div>}
    </div>
  );
}

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className = '', ...rest }, ref) => <input ref={ref} className={`input ${className}`} {...rest} />,
);

export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className = '', children, ...rest }, ref) => <select ref={ref} className={`select ${className}`} {...rest}>{children}</select>,
);

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className = '', ...rest }, ref) => <textarea ref={ref} className={`textarea ${className}`} {...rest} />,
);

export function SearchField({ className = '', ...rest }: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className={`search-field ${className}`}>
      <span className="search-icon" aria-hidden="true">⌕</span>
      <Input type="search" aria-label={rest['aria-label'] ?? rest.placeholder ?? 'Search'} {...rest} />
    </div>
  );
}

export function FormRow({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`form-row ${className}`}>{children}</div>;
}

export function FormActions({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`form-actions ${className}`}>{children}</div>;
}

/* ============================================================
   Alert
   ============================================================ */
type AlertTone = 'info' | 'success' | 'warning' | 'error';
export function Alert({ tone = 'info', className = '', children }: { tone?: AlertTone; className?: string; children: React.ReactNode }) {
  // Errors/warnings interrupt (role=alert); info/success announce politely (role=status).
  const role = tone === 'error' || tone === 'warning' ? 'alert' : 'status';
  return <div role={role} className={`alert alert-${tone} ${className}`}>{children}</div>;
}

/* ============================================================
   Error boundary
   ============================================================ */
/**
 * ErrorBoundary — a render crash inside the boundary shows a recoverable
 * error card instead of white-screening the whole app. Use around
 * server-driven views (e.g. analysis results) where the payload shape can
 * drift from what the client expects.
 */
interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Shown above the fallback; defaults to a generic explanation. */
  title?: string;
  /** Called to reset the view (e.g. clear the selection that crashed). */
  onReset?: () => void;
  resetLabel?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error): void {
    // eslint-disable-next-line no-console
    console.error('[ui] ErrorBoundary caught', error);
  }

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div role="alert" className="alert alert-error">
        <strong>{this.props.title ?? 'Something went wrong showing this.'}</strong>
        <p className="text-sm" style={{ margin: 'var(--s2) 0 0' }}>
          The data for this view didn&apos;t load in the expected shape. Your work is safe —
          try again or pick something else.
        </p>
        {this.props.onReset && (
          <div style={{ marginTop: 'var(--s3)' }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                this.setState({ error: null });
                this.props.onReset?.();
              }}
            >
              {this.props.resetLabel ?? 'Reset view'}
            </Button>
          </div>
        )}
      </div>
    );
  }
}

/* ============================================================
   Empty / loading
   ============================================================ */
export function EmptyState({ icon = '◌', title, body, action }: { icon?: React.ReactNode; title: string; body?: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="empty-state">
      <div className="empty-icon" aria-hidden="true">{icon}</div>
      <h3>{title}</h3>
      {body && <p>{body}</p>}
      {action}
    </div>
  );
}

export function LoadingScreen({ label = 'Loading…' }: { label?: string }) {  return (
    <div className="loading-screen" role="status" aria-live="polite">
      <div className="spinner" aria-hidden="true" />
      <div>{label}</div>
    </div>
  );
}

export function InlineLoading({ label = 'Loading…' }: { label?: string }) {
  return (
    <span className="loading-inline" role="status">
      <span className="spinner" aria-hidden="true" />{label}
    </span>
  );
}

export function Skeleton({ width = '100%', height = 14, className = '' }: { width?: string | number; height?: number; className?: string }) {
  return <div className={`skeleton ${className}`} style={{ width, height }} aria-hidden="true" />;
}

/* ============================================================
   Modal
   ============================================================ */
export function Modal({ title, onClose, children, wide }: { title: React.ReactNode; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  const dialogRef = useDialogFocus(true, onClose);
  const titleId = useId();
  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="modal" ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true"
        aria-labelledby={titleId} style={wide ? { maxWidth: 760 } : undefined}
      >
        <div className="modal-header">
          <div className="modal-title" id={titleId}>{title}</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close dialog">✕</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

/* ============================================================
   Drawer
   ============================================================ */
export function Drawer({ title, sub, onClose, footer, children }: {
  title: React.ReactNode; sub?: React.ReactNode; onClose: () => void;
  footer?: React.ReactNode; children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);
  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : 'Panel'}>
        <div className="drawer-header">
          <div>
            <div className="drawer-title">{title}</div>
            {sub && <div className="text-sm text-muted mt-2">{sub}</div>}
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close panel">✕</button>
        </div>
        <div className="drawer-body">{children}</div>
        {footer && <div className="drawer-footer">{footer}</div>}
      </aside>
    </>
  );
}

/* ============================================================
   Tabs / Segmented
   ============================================================ */
export function Tabs<T extends string>({ tabs, value, onChange, className = '', label }: {
  tabs: { value: T; label: React.ReactNode }[]; value: T; onChange: (v: T) => void; className?: string; label?: string;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next !== null) {
      e.preventDefault();
      onChange(tabs[next].value);
      refs.current[next]?.focus();
    }
  };
  return (
    <div className={`tabs ${className}`} role="tablist" aria-label={label}>
      {tabs.map((t, i) => (
        <button
          key={t.value} type="button" role="tab" aria-selected={value === t.value}
          tabIndex={value === t.value ? 0 : -1}
          ref={(el) => { refs.current[i] = el; }}
          className="tab" onClick={() => onChange(t.value)} onKeyDown={(e) => onKeyDown(e, i)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string | number>({ options, value, onChange, label }: {
  options: { value: T; label: React.ReactNode }[]; value: T; onChange: (v: T) => void; label?: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={String(o.value)} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ============================================================
   ScoreRing
   ============================================================ */
export function ScoreRing({ score, size = 56, small, label = 'Score' }: { score: number; size?: number; small?: boolean; label?: string }) {
  const s = small ? 36 : size;
  const r = (s - 8) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  // Animate the arc from 0 on mount / score change (CSS transition; snaps instantly under reduced-motion).
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(pct));
    return () => cancelAnimationFrame(raf);
  }, [pct]);
  const color = pct >= 80 ? 'var(--teal-dark)' : pct >= 60 ? 'var(--teal)' : pct >= 40 ? 'var(--amber)' : 'var(--ink-3)';
  return (
    <span className={`score-ring${small ? ' sm' : ''}`} role="img" aria-label={`${label} ${Math.round(pct)} out of 100`}>
      <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} aria-hidden="true">
        <circle cx={s / 2} cy={s / 2} r={r} fill="none" stroke="var(--line)" strokeWidth={small ? 4 : 6} />
        <circle
          className="ring-arc"
          cx={s / 2} cy={s / 2} r={r} fill="none" stroke={color} strokeWidth={small ? 4 : 6}
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (c * shown) / 100}
        />
      </svg>
      <span className="ring-value">{Math.round(pct)}</span>
    </span>
  );
}

/* ============================================================
   DeadlineChip — amber for deadlines/risk, red for overdue
   ============================================================ */
export function DeadlineChip({ date, label, kind }: { date?: string | null; label?: string; kind?: string | null }) {
  // Deadline states (release acceptance): a non-dated state renders
  // its shared label, not the generic "No deadline".
  if (!date) return <span className="text-faint text-sm">{deadlineStateLabel(kind) ?? 'No deadline'}</span>;
  // R2-BR-D17: calendar-day arithmetic + calendar-date title via
  // lib/dateOnly, so the chip agrees with every other surface's count.
  const days = daysUntilCalendar(date) ?? 0;
  const tone = days < 0 ? 'danger' : days <= 14 ? 'warning' : 'neutral';
  const text = label ?? (days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Due today' : days === 1 ? 'Due tomorrow' : `${days}d left`);
  const full = formatDateOnly(date, { style: 'short' }) ?? '';
  return (
    <span className="deadline-chip" title={`Deadline: ${full}`}>
      <svg className="cal-ico" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="1.5" y="2.5" width="13" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M1.5 6.5h13M5 1v3M11 1v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <Badge tone={tone}>{text}</Badge>
    </span>
  );
}

/* ============================================================
   Avatar
   ============================================================ */
export function Avatar({ name, large }: { name?: string | null; large?: boolean }) {
  const initials = (name ?? '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  return <span className={`avatar${large ? ' lg' : ''}`} aria-hidden="true">{initials}</span>;
}

/* ============================================================
   Timeline
   ============================================================ */
export interface TimelineEvent { title: React.ReactNode; meta?: React.ReactNode; tone?: 'done' | 'risk' | 'blocked' | 'default'; }
export function Timeline({ events }: { events: TimelineEvent[] }) {
  return (
    <ol className="timeline">
      {events.map((e, i) => (
        <li key={i} className="timeline-item">
          <span className={`timeline-dot${e.tone && e.tone !== 'default' ? ` ${e.tone}` : ''}`} aria-hidden="true" />
          <div className="timeline-title">{e.title}</div>
          {e.meta && <div className="timeline-meta">{e.meta}</div>}
        </li>
      ))}
    </ol>
  );
}

/* ============================================================
   Checklist
   ============================================================ */
export interface CheckItem { id: string; label: string; note?: string; done: boolean; }
export function Checklist({ items, onToggle }: { items: CheckItem[]; onToggle: (id: string) => void }) {
  return (
    <ul className="checklist">
      {items.map((it) => (
        <li key={it.id}>
          <div
            className={`check-item${it.done ? ' done' : ''}`} role="checkbox" aria-checked={it.done}
            tabIndex={0} onClick={() => onToggle(it.id)}
            onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onToggle(it.id); } }}
          >
            <span className="check-box" aria-hidden="true">{it.done ? '✓' : ''}</span>
            <span>
              <span className="check-label">{it.label}</span>
              {it.note && <div className="check-note">{it.note}</div>}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ============================================================
   Filter chips
   ============================================================ */
export function FilterChips<T extends string>({ options, value, onChange, label }: {
  options: { value: T; label: React.ReactNode }[]; value: T; onChange: (v: T) => void; label?: string;
}) {
  return (
    <div className="chip-row" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" className="filter-chip" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ============================================================
   Stat
   ============================================================ */
export function Stat({ label, value, sub, to }: { label: string; value: React.ReactNode; sub?: React.ReactNode; to?: string }) {
  const inner = (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
  if (to) return <a href={to} className="stat-link" onClick={(e) => { e.preventDefault(); window.location.hash = ''; }}>{inner}</a>;
  return inner;
}

/* ============================================================
   ProgressBar / BarChart
   ============================================================ */
export function ProgressBar({ value, tone = '', className = '', label }: { value: number; tone?: 'navy' | 'amber' | 'green' | ''; className?: string; label: string }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className={`progress ${className}`} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <div className={`progress-fill${tone ? ` ${tone}` : ''}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function BarChart({ rows, max }: { rows: { label: string; value: number; display?: string }[]; max?: number }) {
  const m = max ?? Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="bar-chart">
      {rows.map((r) => (
        <div className="bar-row" key={r.label}>
          <span className="text-sm">{r.label}</span>
          <span className="bar-track"><span className="bar-fill" style={{ width: `${(r.value / m) * 100}%` }} /></span>
          <span className="bar-value">{r.display ?? r.value}</span>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   Pagination
   ============================================================ */
export function Pagination({ page, totalPages, total, pageSize, onPage }: {
  page: number; totalPages: number; total: number; pageSize: number; onPage: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="pagination">
      <div className="pagination-info">Showing {from}–{to} of {total}</div>
      <div className="pagination-btns">
        <Button size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>← Prev</Button>
        <Button size="sm" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>Next →</Button>
      </div>
    </div>
  );
}

/* ============================================================
   Page / section headers
   ============================================================ */
export function PageHeader({ eyebrow, title, desc, actions }: { eyebrow?: string; title: string; desc?: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div className="page-header">
      <div>
        {eyebrow && <div className="page-eyebrow">{eyebrow}</div>}
        <h1 className="page-title">{title}</h1>
        {desc && <p className="page-desc">{desc}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

export function SectionHead({ index, title, sub, actions }: { index?: number; title: string; sub?: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div className="section-head">
      <div className="stack" style={{ flexDirection: 'row', alignItems: 'center' }}>
        {typeof index === 'number' && <span className="section-index" aria-hidden="true">{index}</span>}
        <div>
          <h2 className="section-title" style={{ marginBottom: sub ? 2 : 0 }}>{title}</h2>
          {sub && <div className="text-sm text-muted">{sub}</div>}
        </div>
      </div>
      {actions && <div className="section-actions">{actions}</div>}
    </div>
  );
}

/* ============================================================
   Dropdown menu
   ============================================================ */
export function Menu({ trigger, label, children, align = 'right' }: {
  trigger: React.ReactNode; label: string; children: React.ReactNode; align?: 'right' | 'left';
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // The trigger is a real button: expose menu semantics on it (not a wrapper div).
  const labelledTrigger = React.isValidElement(trigger)
    ? React.cloneElement(trigger as React.ReactElement<Record<string, unknown>>, {
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-label': (trigger.props as { 'aria-label'?: string })['aria-label'] ?? label,
        ref: triggerRef,
      })
    : trigger;

  const menuItems = useCallback(() => {
    const menu = menuRef.current;
    if (!menu) return [] as HTMLButtonElement[];
    return Array.from(menu.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]:not([disabled])'));
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
      const items = menuItems();
      if (items.length === 0) return;
      e.preventDefault();
      const idx = items.indexOf(document.activeElement as HTMLButtonElement);
      let next = 0;
      if (e.key === 'ArrowDown') next = (idx + 1) % items.length;
      else if (e.key === 'ArrowUp') next = (idx - 1 + items.length) % items.length;
      else if (e.key === 'End') next = items.length - 1;
      items[next]?.focus();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    // Move focus into the menu on open.
    const t = window.setTimeout(() => menuItems()[0]?.focus(), 0);
    return () => { window.clearTimeout(t); document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open, menuItems]);
  return (
    <div className="menu-wrap" ref={ref}>
      <div className="menu-trigger-wrap" onClick={() => setOpen((o) => !o)}>
        {labelledTrigger}
      </div>
      {open && (
        <div ref={menuRef} className="menu" role="menu" aria-label={label} style={align === 'left' ? { right: 'auto', left: 0 } : undefined}>
          {React.Children.map(children, (c) =>
            React.isValidElement(c) ? React.cloneElement(c as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>, {
              onClick: (e: React.MouseEvent) => {
                (c.props as { onClick?: (e: React.MouseEvent) => void }).onClick?.(e);
                setOpen(false);
                triggerRef.current?.focus();
              },
            }) : c,
          )}
        </div>
      )}
    </div>
  );
}

export function MenuItem({ danger, children, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { danger?: boolean }) {
  return <button type="button" role="menuitem" className={`menu-item${danger ? ' danger' : ''}`} {...rest}>{children}</button>;
}

export function MenuHead({ children }: { children: React.ReactNode }) {
  return <div className="menu-head">{children}</div>;
}

export function MenuSep() {
  return <div className="menu-sep" />;
}

/* ============================================================
   Toast (simple context)
   ============================================================ */
interface Toast { id: number; message: string; tone?: 'success' | 'error'; }
const ToastCtx = createContext<(message: string, tone?: 'success' | 'error') => void>(() => {});
export const useToast = () => useContext(ToastCtx);

let toastId = 0;
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((message: string, tone?: 'success' | 'error') => {
    const id = ++toastId;
    setToasts((t) => [...t, { id, message, tone }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4200);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-host" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast${t.tone ? ` toast-${t.tone}` : ''}`}>{t.message}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/* ============================================================
   Misc
   ============================================================ */
export function Kbd({ children }: { children: React.ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}

export function Tip({ tip, children }: { tip: string; children: React.ReactNode }) {
  return <span data-tip={tip}>{children}</span>;
}
